import React, { useState } from "react";
import { Button, Form, Input } from "reactstrap";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

import logo from "../../assets/img/Logo-png.svg";
import { adminlogin } from "../../api/ApiServices";

const Login = () => {
  const navigate = useNavigate();

  // ============================================
  // STATE
  // ============================================

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const [fieldError, setFieldError] = useState({
    email: "",
    password: "",
  });

  // ============================================
  // EMAIL CHANGE
  // ============================================

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    setFieldError((prev) => ({
      ...prev,
      email: "",
    }));
  };

  // ============================================
  // PASSWORD CHANGE
  // ============================================

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    setFieldError((prev) => ({
      ...prev,
      password: "",
    }));
  };

  // ============================================
  // LOGIN
  // ============================================

  const handleLogin = async (e) => {
    e.preventDefault();

    if (loading) {
      return;
    }

    setFieldError({
      email: "",
      password: "",
    });

    const cleanEmail = email.trim();
    const cleanPassword = password;

    // ============================================
    // FRONTEND VALIDATION
    // ============================================

    if (!cleanEmail && !cleanPassword) {
      setFieldError({
        email: "Email address is required.",
        password: "Password is required.",
      });
      return;
    }

    if (!cleanEmail) {
      setFieldError({
        email: "Email address is required.",
        password: "",
      });
      return;
    }

    if (!cleanPassword) {
      setFieldError({
        email: "",
        password: "Password is required.",
      });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(cleanEmail)) {
      setFieldError({
        email: "Please enter a valid email address.",
        password: "",
      });
      return;
    }

    // ============================================
    // START LOADING
    // ============================================

    setLoading(true);

    try {
      // ==========================================
      // API REQUEST
      // ==========================================

      const response = await adminlogin({
        email: cleanEmail,
        password: cleanPassword,
      });

      // ==========================================
      // SUCCESS - DIRECT REDIRECT (NO MESSAGE)
      // ==========================================

      if (response?.success === true && response?.token) {
        // ----------------------------------------
        // Save Token
        // ----------------------------------------
        if (response?.token) {
          localStorage.setItem("token", response.token);
        }

        // ----------------------------------------
        // Save User Data
        // ----------------------------------------
        if (response?.data) {
          localStorage.setItem("userData", JSON.stringify(response.data));
          localStorage.setItem(
            "userName",
            response.data?.fullName || response.data?.au_name || "Admin"
          );
          if (response.data?.roleName) {
            localStorage.setItem("userRole", response.data.roleName);
          }
          if (Array.isArray(response.data?.permissions)) {
            localStorage.setItem(
              "userPermissions",
              JSON.stringify(response.data.permissions)
            );
          }
        }

        // ----------------------------------------
        // Login Status
        // ----------------------------------------
        localStorage.setItem("isLoggedIn", "true");

        // ========================================
        // DIRECT REDIRECT - NO SWAL MESSAGE
        // ========================================
        navigate("/admin/index", { replace: true });
        return;
      }

      // ==========================================
      // SUCCESS FALSE - SHOW ERROR
      // ==========================================

      const message = response?.message || response?.error || "Invalid email or password.";
      const lowerMessage = String(message).toLowerCase();

      // Wrong Password
      if (
        lowerMessage.includes("password") ||
        lowerMessage.includes("credential") ||
        lowerMessage.includes("incorrect") ||
        lowerMessage.includes("invalid")
      ) {
        setFieldError({
          email: "",
          password: message,
        });
        return;
      }

      // Wrong Email / User
      if (
        lowerMessage.includes("email") ||
        lowerMessage.includes("user") ||
        lowerMessage.includes("account") ||
        lowerMessage.includes("not found")
      ) {
        setFieldError({
          email: message,
          password: "",
        });
        return;
      }

      // Other Errors - Show Error Swal
      await Swal.fire({
        icon: "error",
        title: "Login Failed",
        text: message,
        confirmButtonColor: "#f5365c",
        confirmButtonText: "Try Again",
      });

      return;
    } catch (error) {
      // ==========================================
      // ERROR HANDLING
      // ==========================================

      const status = error?.response?.status;
      const responseData = error?.response?.data;
      const message = responseData?.message || responseData?.error || "Something went wrong. Please try again.";
      const lowerMessage = String(message).toLowerCase();

      // 401 Unauthorized
      if (status === 401) {
        if (
          lowerMessage.includes("password") ||
          lowerMessage.includes("credential") ||
          lowerMessage.includes("incorrect") ||
          lowerMessage.includes("invalid")
        ) {
          setFieldError({
            email: "",
            password: message,
          });
          return;
        }
        setFieldError({
          email: "",
          password: "Invalid email or password.",
        });
        return;
      }

      // 404 Not Found
      if (status === 404) {
        setFieldError({
          email: "Account not found. Please check your email.",
          password: "",
        });
        return;
      }

      // 403 Forbidden
      if (status === 403) {
        await Swal.fire({
          icon: "error",
          title: "Account Blocked",
          text: message || "Your account is inactive or blocked. Please contact support.",
          confirmButtonColor: "#f5365c",
          confirmButtonText: "OK",
        });
        return;
      }

      // Email/User Error
      if (
        lowerMessage.includes("email") ||
        lowerMessage.includes("user") ||
        lowerMessage.includes("account") ||
        lowerMessage.includes("not found")
      ) {
        setFieldError({
          email: message,
          password: "",
        });
        return;
      }

      // Server / Network Error
      await Swal.fire({
        icon: "error",
        title: "Login Error",
        text: message,
        confirmButtonColor: "#f5365c",
        confirmButtonText: "OK",
      });
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // RENDER
  // ============================================

  return (
    <div className="login-card">

      <img
        src={logo}
        alt="Localmate Logo"
        className="login-logo"
      />

      <div className="lock-icon-wrapper">
        <i className="fas fa-lock-open"></i>
      </div>

      <h1 className="admin-login-title">
        Area Track Admin
      </h1>

      <p className="admin-login-subtitle">
        Meet the city through someone who knows it.
      </p>

      <Form onSubmit={handleLogin}>

        {/* EMAIL */}
        <div className="custom-form-group">

          <label className="custom-label">
            Email Address
          </label>

          <div
            className="custom-input-group"
            style={{
              borderColor: fieldError.email ? "#f5365c" : "",
            }}
          >

            <i className="far fa-envelope"></i>

            <Input
              type="email"
              value={email}
              onChange={handleEmailChange}
              autoComplete="email"
              disabled={loading}
              placeholder="Enter your email"
            />

          </div>

          {fieldError.email && (
            <div
              style={{
                color: "#f5365c",
                fontSize: "12px",
                marginTop: "5px",
              }}
            >
              <i className="fas fa-exclamation-circle mr-1"></i>{" "}
              {fieldError.email}
            </div>
          )}

        </div>

        {/* PASSWORD */}
        <div className="custom-form-group">

          <div className="custom-label-row">

            <label className="custom-label">
              Password
            </label>

            <a
              className="forgot-password-link"
              href="/auth/forgot-password"
              onClick={(e) => {
                if (loading) {
                  e.preventDefault();
                }
              }}
            >
              Forgot password?
            </a>

          </div>

          <div
            className="custom-input-group"
            style={{
              borderColor: fieldError.password ? "#f5365c" : "",
            }}
          >

            <i className="fas fa-lock"></i>

            <Input
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={handlePasswordChange}
              autoComplete="current-password"
              disabled={loading}
              placeholder="Enter your password"
            />

            <i
              className={showPassword ? "fas fa-eye-slash" : "fas fa-eye"}
              onClick={() => {
                if (!loading) {
                  setShowPassword((prev) => !prev);
                }
              }}
              style={{
                cursor: loading ? "not-allowed" : "pointer",
                marginLeft: "10px",
                color: "#8898aa",
              }}
            />

          </div>

          {fieldError.password && (
            <div
              style={{
                color: "#f5365c",
                fontSize: "12px",
                marginTop: "5px",
              }}
            >
              <i className="fas fa-exclamation-circle mr-1"></i>{" "}
              {fieldError.password}
            </div>
          )}

        </div>

        {/* SIGN IN BUTTON */}
        <Button
          className="sign-in-btn"
          type="submit"
          disabled={loading}
        >

          {loading ? (
            <>
              <i className="fas fa-spinner fa-spin mr-2 gradient-color-loader"></i>
              Signing in...
            </>
          ) : (
            <>
              Sign In
              <i className="fas fa-arrow-right ml-2"></i>
            </>
          )}

        </Button>

      </Form>
    </div>
  );
};

export default Login;